// matchSheet and currentDraw store matches flat; orderOfPlaySchedule wraps
// each match in a schedule slot ({ matchDetails: {...}, ... }). Centralizes
// that shape difference so callers don't reach into either form directly.
export const readMatchSlice = (entry) => {
    if (!entry) {
        return null;
    }
    if (entry.matchDetails) {
        return {
            id: entry.matchDetails.id,
            drawId: entry.matchDetails.drawId,
            target: entry.matchDetails,
            nested: true,
        };
    }
    return { id: entry.id, drawId: entry.drawId, target: entry, nested: false };
};

const matchInDrawData = (drawData, matchId) => {
    if (!drawData?.rounds) {
        return null;
    }
    let found = null;
    drawData.rounds.some(round => {
        found = (round.matches || []).find(m => readMatchSlice(m)?.id === matchId) || null;
        return found !== null;
    });
    return found;
};

/**
 * Locate a match across the event-profile slices.
 * Returns { drawId, sliceKeys } where sliceKeys lists the slices containing the match
 * ('currentDraw' | 'matchSheet' | 'orderOfPlaySchedule').
 * drawId is read from the first slice's match record (currentDraw → matchSheet → OOP).
 */
export const findMatchAcrossSlices = (matchId, { matchSheet, orderOfPlaySchedule, currentDraw }) => {
    const sliceKeys = [];
    let drawId = null;

    const drawMatch = matchInDrawData(currentDraw?.data, matchId);
    if (drawMatch) {
        sliceKeys.push('currentDraw');
        drawId = drawId || readMatchSlice(drawMatch).drawId || currentDraw.data?.id;
    }

    const matchSheetMatch = (matchSheet?.matches || []).find(m => readMatchSlice(m)?.id === matchId);
    if (matchSheetMatch) {
        sliceKeys.push('matchSheet');
        drawId = drawId || readMatchSlice(matchSheetMatch).drawId;
    }

    const oopMatch = (orderOfPlaySchedule?.data?.matches || []).find(m => readMatchSlice(m)?.id === matchId);
    if (oopMatch) {
        sliceKeys.push('orderOfPlaySchedule');
        drawId = drawId || readMatchSlice(oopMatch).drawId;
    }

    return { drawId, sliceKeys };
};

/**
 * Locate the existing slice match object for `matchId`, returning the inner
 * match (unwrapping OOP's matchDetails) and the containing round.id when found
 * in currentDraw. The roundId is needed by EVENT_PROFILE_GET_MATCH_SUCCESS to
 * find the right round in currentDraw; matchSheet/OOP patch by id alone.
 */
export const findMatchInSlices = (matchId, { matchSheet, orderOfPlaySchedule, currentDraw }) => {
    const rounds = currentDraw?.data?.rounds || [];
    const drawRound = rounds.find(round => (round.matches || []).some(entry => readMatchSlice(entry)?.id === matchId));
    if (drawRound) {
        const drawEntry = drawRound.matches.find(entry => readMatchSlice(entry)?.id === matchId);
        return { match: readMatchSlice(drawEntry).target, roundId: drawRound.id };
    }

    const matchSheetMatch = (matchSheet?.matches || []).find(m => readMatchSlice(m)?.id === matchId);
    if (matchSheetMatch) {
        return { match: readMatchSlice(matchSheetMatch).target, roundId: null };
    }

    const oopMatch = (orderOfPlaySchedule?.data?.matches || []).find(m => readMatchSlice(m)?.id === matchId);
    if (oopMatch) {
        return { match: readMatchSlice(oopMatch).target, roundId: null };
    }

    return null;
};
